import type { InfiniteData } from "@tanstack/vue-query";
import type { MaybeRefOrGetter } from "vue";
import type { HydraCollection, PartialHydraCollection } from "../Api/Type/hydra.interface";
import type { HydraSource } from "./hydraSource";
import type { provideSearchable } from "./searchable";
import type { provideSortable } from "./sortable";
import { useInfiniteQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, toValue } from "vue";
import { patchHydraQueries, removeHydraQueryItem } from "./hydraCacheMutators";
import {
  applyPageParams,
  axiosErrorToState,
  DEFAULT_PER_PAGE,
  extractPageFromHydraNext,
  firstPageParamOf,
  isPartialPage,
  OVERVIEW_MAX_PAGES,
  paramsToKey,
  previousHydraPageParam,
  useKnownTotalItems,
} from "./hydraInfinite";

export interface HydraInfiniteOptions<T> {
  perPage?: number
  maxPages?: number
  // Only its URL parameters are read, so the search may filter on relation paths (`createdBy.name`) that are not
  // fields of the row.
  searchable?: Pick<ReturnType<typeof provideSearchable<T>>, "setSearchUrlParams">
  sortable?: ReturnType<typeof provideSortable>
  buildParams?: (params: URLSearchParams) => void
  enabled?: MaybeRefOrGetter<boolean>
  refetchInterval?: MaybeRefOrGetter<number | false>
  staleTime?: number
}

type HydraPage<T> = HydraCollection<T> | PartialHydraCollection<T>;

export function useHydraInfiniteCollection<T extends { "@id": string }>(
  source: MaybeRefOrGetter<HydraSource<T> | null>,
  options: HydraInfiniteOptions<T> = {},
) {
  const queryClient = useQueryClient();
  const perPage = options.perPage ?? DEFAULT_PER_PAGE;
  const maxPages = options.maxPages ?? OVERVIEW_MAX_PAGES;

  function buildAllParams(page: number, partial = page > 1): URLSearchParams {
    const params = new URLSearchParams();
    applyPageParams(params, page, perPage, partial);
    options.sortable?.setSortUrlParams(params);
    options.searchable?.setSearchUrlParams(params);
    options.buildParams?.(params);
    return params;
  }

  const resolvedSource = computed(() => toValue(source));
  const enabled = computed(() => resolvedSource.value != null && toValue(options.enabled ?? true));
  const paramsKey = computed(() => {
    if (!enabled.value) {
      return "disabled";
    }
    return paramsToKey(buildAllParams(1));
  });
  const queryKey = computed((): readonly unknown[] => {
    const current = resolvedSource.value;
    if (current == null) {
      return ["disabled"];
    }
    return [...current.key, "infinite", paramsKey.value];
  });

  const query = useInfiniteQuery(computed(() => ({
    queryKey: queryKey.value,
    enabled: enabled.value,
    initialPageParam: 1,
    maxPages,
    staleTime: options.staleTime,
    // Only when the source has its own: an undefined `retry` would replace the default of the query client.
    ...(resolvedSource.value?.retry ? { retry: resolvedSource.value.retry } : {}),
    refetchInterval: toValue(options.refetchInterval ?? false),
    queryFn: async ({ pageParam, signal }: { pageParam: number, signal?: AbortSignal }) => {
      const current = resolvedSource.value;
      if (current == null) {
        throw new Error("HydraSource is not available");
      }
      const firstCachedPage = firstPageParamOf(
        queryClient.getQueryData<InfiniteData<HydraPage<T>, unknown>>(queryKey.value),
      );
      return current.load(
        buildAllParams(pageParam, isPartialPage(pageParam, firstCachedPage)),
        signal ?? new AbortController().signal,
      );
    },
    getNextPageParam: (last: PartialHydraCollection<T>) => extractPageFromHydraNext(last.view?.next),
    getPreviousPageParam: (_first: PartialHydraCollection<T>, _pages: unknown, firstPageParam: number) =>
      previousHydraPageParam(firstPageParam),
    shallow: true,
  })));

  const items = computed<T[]>(() =>
    query.data.value?.pages.flatMap(page => page.member as T[]) ?? [],
  );
  const totalItems = useKnownTotalItems(query.data);
  const firstPageParam = computed(() => firstPageParamOf(query.data.value));
  const errorState = computed(() => {
    if (!query.error.value) {
      return { errorCode: undefined, errorMsg: "" };
    }
    return axiosErrorToState(query.error.value);
  });
  const hasError = computed(() => query.isError.value && query.data.value === undefined);

  function sourceKey(): readonly unknown[] {
    return resolvedSource.value?.key ?? ["disabled"];
  }

  function patchItem(iri: string, patch: Partial<T>): void {
    patchHydraQueries(queryClient, sourceKey(), iri, patch);
  }

  function removeItem(iri: string): void {
    removeHydraQueryItem(queryClient, sourceKey(), iri);
  }

  function invalidate() {
    return queryClient.invalidateQueries({ queryKey: sourceKey() });
  }

  return {
    items,
    totalItems,
    loading: computed(() => query.isPending.value && enabled.value),
    isFetching: query.isFetching,
    isRefetching: query.isRefetching,
    /** When the cached pages last changed: a fetch, or a patch through patchItem/removeItem (0 before any). */
    dataUpdatedAt: query.dataUpdatedAt,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    hasPreviousPage: query.hasPreviousPage,
    fetchPreviousPage: query.fetchPreviousPage,
    isFetchingPreviousPage: query.isFetchingPreviousPage,
    firstPageParam,
    hasError,
    errorCode: computed(() => errorState.value.errorCode),
    errorMsg: computed(() => errorState.value.errorMsg),
    refetch: query.refetch,
    queryKey,
    patchItem,
    removeItem,
    invalidate,
  };
}
